const { formatCurrency, formatDate, formatTime, getShortTransactionLogId } = require('./helpers')
const { getAgentTopupProfitCents } = require('./agent-rate-conversions')

const AGENT_TRANSACTION_LOG_TYPE_OPTIONS = [
  { text: 'Purchased credit', value: 'purchased_credit' },
  { text: 'Top up', value: 'passenger_credit' },
  { text: 'Refund', value: 'refund' },
  { text: 'Credit refund', value: 'passenger_credit_reversal' },
  { text: 'Deduction', value: 'passenger_credit_deduction' }
]

const LOG_TYPE_ICON_DATA = {
  purchased_credit: {
    icon: 'fas fa-plus',
    color: '#000',
    label: 'purchased credit'
  },
  passenger_credit: {
    icon: 'fas fa-paper-plane',
    color: '#000',
    label: 'top up'
  },
  refund: {
    icon: 'fas fa-undo',
    color: '#000',
    label: 'refund'
  },
  passenger_credit_reversal: {
    icon: 'fas fa-undo',
    color: '#000',
    label: 'credit refund'
  },
  passenger_credit_deduction: {
    icon: 'fas fa-share',
    color: '#000',
    label: 'deduction'
  }
}

const DEFAULT_LOG_TYPE_ICON_DATA = {
  icon: 'fas fa-circle',
  color: '#000',
  label: ''
}

const LOG_STATUS_ICON_DATA = {
  confirmed: { icon: 'fas fa-check', color: '#4CAF50' },
  completed: { icon: 'fas fa-check', color: '#4CAF50' },
  processing: { icon: 'far fa-hourglass-half', color: '#000' },
  pending: { icon: 'far fa-hourglass-half', color: '#000' },
  cancelled: { icon: 'far fa-ban', color: '#000' },
  rejected: { icon: 'fas fa-exclamation-triangle', color: '#FFC107' },
  error: { icon: 'fas fa-exclamation-triangle', color: '#f65122' },
  timedout: { icon: 'fas fa-hourglass-end', color: '#f65122' }
}

const DEFAULT_LOG_STATUS_ICON_DATA = {
  icon: 'fas fa-circle',
  color: '#000'
}

const LOG_STATUS_LABELS = {
  confirmed: 'confirmed',
  completed: 'completed',
  processing: 'processing',
  pending: 'pending',
  cancelled: 'cancelled',
  rejected: 'rejected',
  error: 'error',
  timedout: 'timed out'
}

const ADMIN_AGENT_TRANSACTION_LOG_TYPE_LABELS = {
  passenger_credit: 'passenger top up'
}

function titleCaseLabel (label) {
  if (!label) {
    return label
  }
  return label.charAt(0).toUpperCase() + label.slice(1)
}

function getLogTypeIconData (logType, labelOverrides) {
  const iconData = LOG_TYPE_ICON_DATA[logType] || DEFAULT_LOG_TYPE_ICON_DATA
  const label = labelOverrides && labelOverrides[logType]
  if (!label) {
    return iconData
  }
  return Object.assign({}, iconData, { label: label })
}

function getAgentTransactionLogTypeOptions (labelOverrides) {
  return AGENT_TRANSACTION_LOG_TYPE_OPTIONS.map(function (option) {
    const label = labelOverrides && labelOverrides[option.value]
    if (!label) {
      return Object.assign({}, option)
    }
    return Object.assign({}, option, { text: titleCaseLabel(label) })
  })
}

function getLogStatusIconData (logStatus) {
  return LOG_STATUS_ICON_DATA[logStatus] || DEFAULT_LOG_STATUS_ICON_DATA
}

function formatLogStatusLabel (logStatus) {
  if (!logStatus) {
    return ''
  }
  return LOG_STATUS_LABELS[logStatus] || String(logStatus).replace(/_/g, ' ')
}

function hasConversionRate (conversionRatePercent) {
  if (conversionRatePercent === null || conversionRatePercent === undefined || conversionRatePercent === '') {
    return false
  }
  return Number.isFinite(Number(conversionRatePercent))
}

function shouldShowPassengerCreditRate (logType, conversionRatePercent) {
  return logType === 'passenger_credit' && hasConversionRate(conversionRatePercent)
}

function formatConversionRatePercentLabel (logType, conversionRatePercent) {
  if (!shouldShowPassengerCreditRate(logType, conversionRatePercent)) {
    return ''
  }
  return Number(conversionRatePercent) + '%'
}

function getDisplayProfitCents (logType, amountAsCents, conversionRatePercent) {
  if (!shouldShowPassengerCreditRate(logType, conversionRatePercent)) {
    return null
  }
  return getAgentTopupProfitCents({
    topupAmountAsCents: amountAsCents,
    conversionRatePercent: conversionRatePercent
  })
}

function formatProfitLabel (logType, amountAsCents, conversionRatePercent, currencyPrefix) {
  const profitCents = getDisplayProfitCents(logType, amountAsCents, conversionRatePercent)
  if (profitCents === null) {
    return ''
  }
  return formatCurrency(profitCents, currencyPrefix || '')
}

function formatDateTimeLabel (date) {
  if (!date) {
    return ''
  }
  return formatDate(date) + ' - ' + formatTime(date)
}

function formatLogMetadataError (logMetadata) {
  if (!logMetadata || logMetadata.error == null || logMetadata.error === '') {
    return ''
  }
  if (typeof logMetadata.error === 'string') {
    return logMetadata.error
  }
  return JSON.stringify(logMetadata.error, null, 2)
}

function formatTransactionErrorLabel (transaction) {
  if (!transaction) {
    return ''
  }
  const logStatus = transaction.logStatus
  if (logStatus !== 'error' && logStatus !== 'timedout') {
    return ''
  }
  const errorText = formatLogMetadataError(transaction.logMetadata)
  if (errorText) {
    return errorText
  }
  if (logStatus === 'timedout') {
    return 'Transaction timed out'
  }
  return ''
}

function relationRecord (transaction, key) {
  if (!transaction) {
    return null
  }
  const value = transaction[key]
  if (typeof value === 'function') {
    return value.call(transaction)
  }
  return value || null
}

function mapAgentTransactionForDisplay (transaction, options) {
  const opts = options || {}
  const currencyPrefix = opts.currencyPrefix || ''
  const logMetadata = (transaction && transaction.logMetadata) || {}
  const momo = relationRecord(transaction, 'agentMobileMoneyAccount')
  const passengerPhone = logMetadata.passengerPhoneNumber || ''
  const momoPhone = (momo && momo.phoneNumber) || ''
  const amount = transaction && transaction.amount
  const conversionRatePercent = transaction && transaction.conversionRatePercent

  return Object.assign({}, transaction, {
    idLabel: transaction && transaction.id ? getShortTransactionLogId(transaction.id) : '',
    passengerPhoneNumberLabel: passengerPhone,
    phoneNumberLabel: momoPhone,
    dateCreatedLabel: formatDateTimeLabel(transaction && transaction.dateCreated),
    dateUpdatedLabel: formatDateTimeLabel(transaction && transaction.dateUpdated),
    amountLabel: formatCurrency(amount, currencyPrefix),
    currencyPrefix: currencyPrefix,
    profileBalanceBeforeLabel: transaction && (transaction.profileBalanceBefore || transaction.profileBalanceBefore === 0)
      ? formatCurrency(transaction.profileBalanceBefore)
      : '',
    profileBalanceAfterLabel: transaction && (transaction.profileBalanceAfter || transaction.profileBalanceAfter === 0)
      ? formatCurrency(transaction.profileBalanceAfter)
      : '',
    conversionRatePercentLabel: formatConversionRatePercentLabel(transaction && transaction.logType, conversionRatePercent),
    profitCents: getDisplayProfitCents(transaction && transaction.logType, amount, conversionRatePercent),
    profitLabel: formatProfitLabel(transaction && transaction.logType, amount, conversionRatePercent, currencyPrefix),
    logTypeIconData: getLogTypeIconData(transaction && transaction.logType, opts.logTypeLabels),
    logStatusIconData: getLogStatusIconData(transaction && transaction.logStatus),
    logStatusLabel: formatLogStatusLabel(transaction && transaction.logStatus),
    errorLabel: formatTransactionErrorLabel(transaction)
  })
}

module.exports = {
  AGENT_TRANSACTION_LOG_TYPE_OPTIONS: AGENT_TRANSACTION_LOG_TYPE_OPTIONS.slice(),
  ADMIN_AGENT_TRANSACTION_LOG_TYPE_LABELS: Object.assign({}, ADMIN_AGENT_TRANSACTION_LOG_TYPE_LABELS),
  getLogTypeIconData,
  getAgentTransactionLogTypeOptions,
  getLogStatusIconData,
  formatLogStatusLabel,
  formatConversionRatePercentLabel,
  formatProfitLabel,
  formatLogMetadataError,
  formatTransactionErrorLabel,
  mapAgentTransactionForDisplay
}
